import React, { useEffect, useContext, useState, useMemo } from "react";
import axios from "axios";
import { useParams, useLocation } from "react-router-dom";

import { channelOptions } from "../core/config/requests";
import { Visible } from "../core/components";
import { FieldGroup, InfiniteList } from "../components";
import { PageHeader, PageLayout, SubTopbar } from "../components/layout";
import { Icon, PageFilterBar, ScopeSelect, ScrollToTop, SearchInput, Segmented, StatePanel, Toggle } from "../components/ui";
import useBreakpoint from "../hooks/useBreakpoint";
import { useAdmins } from "../hooks/admins";
import { useOrdersTabs, ORDERS_ACTIONS, NEW_LOOKBOOK_ACTION, REQUEST_LOOKBOOK_FEATURE } from "./Orders/ordersTabs";
import { useStoreWriteGuard } from "../helpers/storeScope";
import { SellItem, SourcedItem } from "../order/components";
import OrderQuotesDrawer from "../order/components/OrderQuotesDrawer";
import { UserContext, useEntitlement } from '../contexts/index.jsx';
import { useAlert } from "react-alert";
import { scrollToRequest } from "../core/utils/formatters";
import {
  RequestLookbookProvider,
  useRequestLookbook,
} from "../requests/lookbook/useRequestLookbook";
import {
  RequestLookbookBar,
  RequestLookbookDock,
} from "../requests/lookbook/RequestLookbookChrome";
import { RequestCoverModal } from "../requests/lookbook/RequestCoverModal";
import { RequestAmountsModal } from "../requests/lookbook/RequestAmountsModal";
import { PickableRequest } from "../requests/lookbook/PickableRequest";
import RequestDictation from "../requests/components/RequestDictation.jsx";
import "./Orders/orders-dark.scss";
import "./Orders/rc-orders-list.scss";
import "../requests/lookbook/request-lookbook.scss";
import { HOME_PATH } from "../core/config/dashboard.js";

// M54F — the toolbar is the shared row (PageFilterBar) and every filter on it is
// a ScopeSelect, the same control Listings / Buy Alerts / Leads / Users use. The
// vocabularies below are unchanged API values; what was the react-select "All …"
// option is now the absence of a selection, so it lives in the placeholder.
const requestScopeOptions = [
  { key: "sell", label: "Sell Requests" },
  { key: "source", label: "Order Requests" },
];

// Grouped: eleven statuses split across the request's own lifecycle and the
// quote's, exactly as the react-select groups had them.
const statusScopeOptions = [
  {
    group: "Request",
    options: [
      { key: "listing_creation", label: "Listing Creation" },
      { key: "new", label: "New" },
      { key: "in_progress", label: "Send Proposal" },
      { key: "negotiation", label: "Negotiation" },
      { key: "follow_up", label: "Follow Up" },
      { key: "deposited", label: "Deposited" },
      { key: "done", label: "Done" },
      { key: "canceled", label: "Canceled" },
    ],
  },
  {
    group: "Quote",
    options: [
      { key: "created", label: "Created" },
      { key: "to_review", label: "To Review" },
      { key: "approved", label: "Approved" },
    ],
  },
];

const channelScopeOptions = channelOptions.map((option) => ({
  key: option,
  label: option,
}));

const ItemComponent = ({ item, isFromUsersPage, selectedOrderId, onOpen }) =>
  item.request_type === "sell" ? (
    <SellItem
      key={`sell_${item.id}`}
      item={item}
      isFromUsersPage={isFromUsersPage}
      selected={selectedOrderId === item.id}
      onOpen={onOpen}
    />
  ) : (
    <SourcedItem
      key={`order_${item.id}`}
      item={item}
      isFromUsersPage={isFromUsersPage}
      selected={selectedOrderId === item.id}
      onOpen={onOpen}
    />
  );

/**
 * While the lookbook overlay is open every row in the list becomes a pick
 * target instead of a link. The wrapper depends only on whether the overlay is
 * open — NOT on what is selected — so toggling a checkbox never remounts the
 * infinite list and never loses the user's scroll position.
 */
const withPicker = (Base) =>
  function PickableListItem(props) {
    return (
      <PickableRequest item={props.item}>
        {/* createElement, not <Base …>: `Base` is a parameter, and the JSX
            usage rule does not count that as a use, so the HOC tripped
            no-unused-vars on its own argument. */}
        {React.createElement(Base, props)}
      </PickableRequest>
    );
  };

// Opt-in time bound. The list and its COUNT(*) otherwise span the whole
// requests table, which is a real part of why this page is slow to load — but a
// default bound is wrong here: reps come to Orders precisely to find older
// deals, and a hidden one would look like a missing record. So "All" is the
// default and nothing changes until the rep chooses to narrow it.
//
// Values are the Kanban's (`RequestService::getPeriodRange` via
// `filters.period`), not new ones. Note what the API means by them: `year` has
// no case there and falls through to the default — the last 12 months, rolling,
// not the calendar year the Analytics endpoint's `year` gives.
// Labels are words, not initials. "M / Q / Y" needed decoding every time, and the last one was
// actively misleading: the API's `year` is a ROLLING twelve months (it has no case in
// `RequestService::getPeriodRange` and falls through to the default), not the calendar year a "Y"
// implies. The control now says what the filter actually does.
// Compact, like the pill row this replaced — "All time / Month / Quarter / 12 months" made the
// control twice the width of every other filter on the row. But not the old bare initials either:
// "M / Q / Y" needed decoding, and "Y" was actively wrong (the API's `year` is a ROLLING twelve
// months — it has no case in `RequestService::getPeriodRange` and falls through to the default).
// A duration reads at a glance and cannot be misread.
const PERIODS = [
  { value: "", label: "All" },
  { value: "last_month", label: "1M" },
  { value: "last_three_month", label: "3M" },
  { value: "year", label: "12M" },
];

// Titles mirror RCAdmin's PAGE_TITLES so the header reads the same whether it
// comes from the layout top bar or from this page's own Topbar.
const PAGE_COPY = {
  merchant: {
    title: "Merchant Orders",
    description:
      "Buy and sell requests raised by merchants, from first enquiry through to completion.",
  },
  dao: {
    title: "Orders",
    description:
      "Track every buy and sell request, its quotes and its status in one place.",
  },
};

// While the New / Edit lookbook overlay is open the page IS the request picker,
// so its header says so.
const LOOKBOOK_COPY = {
  title: "Request Lookbook",
  description:
    "Tick the requests to include, then drag to set their order in the PDF.",
};

const RequestsView = ({ user, isFromUsersPage = false }) => {
  const alert = useAlert();
  const { notifications, seeNotification } = useContext(UserContext);
  const lookbook = useRequestLookbook();
  // A request is created against one store; from "Master Store" there is no
  // single store to file it under. See useStoreWriteGuard.
  const storeGuard = useStoreWriteGuard().guardProps;
  // 900px — the line where the app swaps to its mobile frame (bottom tab bar).
  const { isAtLeastMd } = useBreakpoint();
  const { owner: ownerParam } = useParams();
  // The Orders tab moved from /requests/dao → /requests/orders. The URL
  // segment is presentational only — the API still expects the `dao` owner
  // filter, so normalise here rather than changing the request contract.
  const owner = ownerParam === "orders" ? "dao" : ownerParam;
  const location = useLocation();
  const tabs = useOrdersTabs();
  const hasRequestLookbooks = useEntitlement(REQUEST_LOOKBOOK_FEATURE);

  // A request lookbook is built by picking from THIS list, so the entry point
  // only makes sense on the standalone Orders page — not on Merchant Orders and
  // not on the cut-down list embedded in a user's profile. It also goes with
  // the plan ("Lookbook generation for Requests").
  const canBuildLookbook =
    hasRequestLookbooks && !isFromUsersPage && owner === "dao" && !!user?.is_admin;

  // `lookbook.active` alone is not enough to drive the UI: the hook turns it on
  // from `?lookbook=<id>` in the URL, and that param can land on a page that
  // cannot save a lookbook. Gating the ROWS on `active` there would make every
  // row inert (see `.rlb-pick__body`) with no bar and no dock to escape from.
  const selecting = canBuildLookbook && lookbook.active;

  const orderActions = useMemo(() => {
    const actions = ORDERS_ACTIONS.map((action) => ({ ...action, ...storeGuard }));
    const withLookbook = canBuildLookbook
      ? [...actions, { ...NEW_LOOKBOOK_ACTION, ...storeGuard, onClick: lookbook.start }]
      : actions;

    // The mic LEADS the row, in front of "+ Sourcing Enquiry": dictation is
    // another way to start a request, so it belongs with the buttons that
    // start one. An element in `actions` stays inline on phones (PageHeader
    // collapses only labelled buttons), so it does not push the primary
    // button into the ⋯ menu.
    return [
      <RequestDictation key="dictate" disabled={!!storeGuard.disabled} title={storeGuard.title} />,
      ...withLookbook,
    ];
  }, [storeGuard, canBuildLookbook, lookbook.start]);

  // Same three list flavours, wrapped once per overlay state — see withPicker.
  const listItemComponents = useMemo(
    () =>
      selecting
        ? {
            both: withPicker(ItemComponent),
            sell: withPicker(SellItem),
            source: withPicker(SourcedItem),
          }
        : { both: ItemComponent, sell: SellItem, source: SourcedItem },
    [selecting]
  );
  const [status, setStatus] = useState("");
  const [request, setRequest] = useState("all");
  const [channel, setChannel] = useState("");
  const [admin, setAdmin] = useState("");
  const [showDeleted, setShowDeleted] = useState(false);
  // The order whose quotes are open in the right-hand panel. One per page, like the User CRM's
  // selected contact — the list holds its column and the panel opens beside it.
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [drawerRefresh, setDrawerRefresh] = useState(0);
  // The list container deliberately does NOT wear the legacy `orders` class: `orders-dark.scss`
  // keys a stack of `!important` rules off it (borders, padding, a bordered card per row) that a
  // class-level override cannot outrank. `rc-olist` starts clean.
  const ordersClassName = "rc-olist";
  const itemProps = {
    selectedOrderId: selectedOrder?.id ?? null,
    onOpen: setSelectedOrder,
  };
  const [search, setSearch] = useState("");
  const [period, setPeriod] = useState("");

  // Was inside `UserFilter` (views/Orders/UserFilter.jsx), which wrapped the same
  // hook around a react-select. The list is flat `{ id, name }`, and its "All
  // Users" entry is the placeholder here, so it is dropped from the rows.
  const { admins } = useAdmins({ forOptions: true, allOption: false });
  const adminScopeOptions = useMemo(
    () => (admins || []).map((a) => ({ key: a.id, label: a.name })),
    [admins]
  );

  useEffect(() => {
    seeNotification([owner === "merchant" ? "merchant_requests" : "requests"]);
  }, [notifications, owner]);

  // `period` is left OUT of the payload on "All" rather than sent empty, so an
  // unbounded load is byte-for-byte the request this page has always made.
  const filters = useMemo(
    () => ({ status, channel, admin, showDeleted, search, owner, ...(period ? { period } : {}) }),
    [status, channel, admin, showDeleted, search, owner, period]
  );

  // F5: a brand-new store sees one friendly empty state with one action instead of
  // "You don't have requests yet". Any filter, search or request-type pick turns it
  // into "No matches" with no action. Merchant Orders and the user-detail tab keep
  // their plain text. The action follows the header's own rules: admins only, and
  // disabled on Master Store (storeGuard), exactly like "+ Sourcing Enquiry".
  const isFiltered = !!(status || channel || admin || showDeleted || search || period || request !== "all");
  const emptyPanel =
    owner === "merchant" || isFromUsersPage ? undefined : isFiltered ? (
      <StatePanel state="no-results" compact />
    ) : (
      <StatePanel
        state="empty"
        title="No client requests yet"
        description="When a client asks you to find or sell a watch, log it here."
        action={user?.is_admin ? { label: "New sourcing enquiry", to: "/request/sourcing", ...storeGuard } : undefined}
      />
    );

  // Refiltering the list means you have moved on from the order the panel is showing — and
  // more than cosmetically: the selected deal is very often no longer IN the list, so the panel
  // sat there describing a row that had scrolled out of existence, with nothing on screen still
  // pointing at it. It closes with the list it belongs to.
  useEffect(() => {
    setSelectedOrder(null);
  }, [filters, request]);

  useEffect(() => {
    async function verifyCode() {
      const searchParams = new URLSearchParams(location.search);
      const verificationCode = searchParams.get("verification_code");

      if (verificationCode) {
        try {
          const { data } = await axios.get(
            `/api/email-verifications/${verificationCode}/is-verified`
          );
          if (data.verified) {
            alert.show("The verification code is invalid or expired");
          }
        } catch (error) {
          console.error("Verification check failed:", error);
        }
      }
    }

    verifyCode();
  }, [location]);

  // Handle scrolling to specific request when coming from quote popup
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const scrollToRequestId = searchParams.get("scrollTo");

    if (scrollToRequestId) {
      // Scroll to the specific request
      scrollToRequest(scrollToRequestId, 1000); // Increased delay to ensure page is loaded
      
      // Clean up the URL parameter to prevent re-scrolling on subsequent renders
      const newSearchParams = new URLSearchParams(location.search);
      newSearchParams.delete("scrollTo");
      const newUrl = `${location.pathname}?${newSearchParams.toString()}`;
      window.history.replaceState({}, '', newUrl);
    }
  }, [location.search]);

  const pageCopy = selecting ? LOOKBOOK_COPY : PAGE_COPY[owner] || PAGE_COPY.dao;

  // The Bootstrap `.container` that used to wrap the body is gone — it was a
  // capped, centred column, so the cards never lined up with the title above
  // them. The gutter comes from the `.rc-page` shell now, once.
  //
  // `orders-dark` stays on `<main id="pages">`: ten selectors in orders-dark.scss
  // need the id and the class on the same element.
  const content = (
    <>
      {/* Shared page chrome. When embedded as a tab inside the user detail page
          the title row is suppressed — only the section tabs are wanted there,
          and `rc-topbar` keeps the legacy nested-tab-bar styling that case still
          relies on. The standalone page needs none of it. */}
      {isFromUsersPage ? (
        <SubTopbar
          className="rc-topbar"
          tabs={tabs}
          actions={user?.is_admin ? orderActions : []}
        />
      ) : (
        <PageHeader
          title={pageCopy.title}
          description={pageCopy.description}
          backTo={HOME_PATH}
          // While the Dashboard is locked this page IS home, so an arrow here
          // would lead back to itself.
          showBackButton={location.pathname !== HOME_PATH}
          // Sticky only where there is room for it. On a phone the header, its
          // tabs and the action row ate a third of the screen and never gave it
          // back, so below 900px the whole top scrolls away and ScrollToTop
          // brings it back in one tap. PageHeader skips its compact-on-scroll
          // observer entirely when it is not sticky, so the title stops
          // resizing too.
          sticky={isAtLeastMd}
          tabs={tabs}
          actions={user?.is_admin ? orderActions : []}
        />
      )}
      <main id="pages" className={`flex-grow-1 mb-5 orders-dark`}>
        {selecting && <RequestLookbookBar />}
        {/* Toolbar — the shared filter row (PageFilterBar, M54D): a fixed-length
            search pill on the left, every filter pinned right, exactly as on Buy
            Alerts, Manage Listings, Leads, User CRM and the Core List. Every
            control keeps its handler and its payload — see Requests.test.jsx. */}
        <PageFilterBar
          className="orders-filterbar"
          label="Filter orders"
          search={user?.is_admin ? (
            /* Uncontrolled on purpose. `DebounceInput` rendered each keystroke
               immediately while debouncing the `onChange` that reaches `search`,
               and `search` is what `filters` — and therefore the list POST — reads.
               `SearchInput` splits those: `onChange` is live, `onSearch` is the
               debounced one. Passing `value` would bind the field to the debounced
               state and make typing lag a beat, so let the primitive hold the text
               and take only `onSearch`. Same 300ms, same single refetch. */
            <SearchInput
              size="lg"
              name="search"
              placeholder="Search"
              debounce={300}
              onSearch={setSearch}
              onClear={() => setSearch("")}
            />
          ) : null}
        >
          {/* Was a hand-rolled pill row (its own comment said so) with a bespoke 44px height and
              a 0.5px border that matched nothing else on the page. `Segmented` is the Style Guide
              V2 primitive for exactly this — one exclusive choice from a short list — and brings
              the radiogroup semantics, roving focus and arrow-key navigation the hand-rolled
              version never had. */}
          <Segmented
            label="Period"
            size="sm"
            className="orders-period"
            value={period}
            onChange={setPeriod}
            options={PERIODS}
          />

          <ScopeSelect
            label="Request type"
            placeholder="All Requests"
            multiple={false}
            options={requestScopeOptions}
            value={request === "all" ? null : request}
            onChange={(next) => setRequest(next ?? "all")}
          />

          <ScopeSelect
            label="Status"
            placeholder="All Statuses"
            multiple={false}
            options={statusScopeOptions}
            value={status || null}
            onChange={(next) => setStatus(next ?? "")}
          />

          <Visible when={user?.is_admin}>
            <>
              <ScopeSelect
                label="User"
                placeholder="All Users"
                multiple={false}
                options={adminScopeOptions}
                value={admin || null}
                onChange={(next) => setAdmin(next ?? "")}
              />

              <Visible when={owner !== "merchant"}>
                <ScopeSelect
                  label="Channel"
                  placeholder="All Channels"
                  multiple={false}
                  options={channelScopeOptions}
                  value={channel || null}
                  onChange={(next) => setChannel(next ?? "")}
                />
              </Visible>

              {/* `Toggle` keeps the same role="switch" + aria-checked contract the
                  hand-rolled control had, and wires the visible label to the button
                  through aria-labelledby, so the accessible name is unchanged. */}
              <Toggle
                size="lg"
                checked={showDeleted}
                onChange={setShowDeleted}
                label="Show hidden records"
              />
            </>
          </Visible>
        </PageFilterBar>

        <section className="orders mt-3">
          <Visible when={request === "all"}>
            <InfiniteList
              className={ordersClassName}
              itemProps={itemProps}
              fetchUrl={`/api/${owner === "merchant" ? "merchant" : "user"}/${
                user.id
              }/requests/both`}
              fetchParams={{ filters }}
              emptyText={`${
                owner === "merchant" ? "Merchants" : "You"
              } don't have requests yet`}
              empty={emptyPanel}
              isFromUsersPage={isFromUsersPage}
              ItemComponent={listItemComponents.both}
            />
          </Visible>

          <Visible when={request === "sell"}>
            <InfiniteList
              className={ordersClassName}
              itemProps={itemProps}
              fetchUrl={`/api/${owner === "merchant" ? "merchant" : "user"}/${
                user.id
              }/requests/sell`}
              fetchParams={{ filters }}
              emptyText={`${
                owner === "merchant" ? "Merchants" : "You"
              } don't have requests yet`}
              empty={emptyPanel}
              isFromUsersPage={isFromUsersPage}
              ItemComponent={listItemComponents.sell}
            />
          </Visible>

          <Visible when={request === "source"}>
            <InfiniteList
              className={ordersClassName}
              itemProps={itemProps}
              fetchUrl={`/api/${owner === "merchant" ? "merchant" : "user"}/${
                user.id
              }/requests/order`}
              fetchParams={{ filters }}
              emptyText={`${
                owner === "merchant" ? "Merchants" : "You"
              } don't have requests yet`}
              empty={emptyPanel}
              isFromUsersPage={isFromUsersPage}
              ItemComponent={listItemComponents.source}
            />
          </Visible>
        </section>
      </main>

      {/* The list is long and the header no longer follows you down on a
          phone, so this is the way back to the filters. */}
      <ScrollToTop />
    </>
  );

  // Standalone, this is a page and gets the standard `.rc-page` shell. Embedded
  // in the user-detail tab it must NOT: it already sits inside that page's
  // `.rc-page`, and a second one would inset the list by the gutter twice.
  return isFromUsersPage ? (
    content
  ) : (
    // `--panel` is what narrows the list to its 60% column (rc-orders-list.scss). It has to be
    // conditional: the ratio used to apply always, so a page with nothing open still gave the
    // panel its 40% — blank on the right, and the list squeezed hard enough that the brand
    // column resolved to zero pixels.
    <PageLayout
      className={`orders-page${selecting ? " orders-page--selecting" : ""}${
        selectedOrder ? " orders-page--panel" : ""
      }`}
    >
      {content}

      {/* One panel for the page, like the User CRM's profile drawer. Bumping `drawerRefresh`
          re-mounts Quotes after a save so the list it shows is the one on the server. */}
      <OrderQuotesDrawer
        key={`${selectedOrder?.id ?? "none"}-${drawerRefresh}`}
        item={selectedOrder}
        open={!!selectedOrder}
        onClose={() => setSelectedOrder(null)}
        onRefresh={() => setDrawerRefresh((n) => n + 1)}
      />

      {selecting && <RequestLookbookDock />}
      {/* The modal owns its own `open` state (Modal's focus trap, Escape and
          scroll lock need to see it close, not vanish). */}
      {selecting && <RequestAmountsModal />}
      {selecting && <RequestCoverModal />}
    </PageLayout>
  );
};

const Requests = (props) => (
  <RequestLookbookProvider>
    <RequestsView {...props} />
  </RequestLookbookProvider>
);

export default Requests;
